import { Auth0DecodedHash, WebAuth } from "auth0-js";
import { AuthState } from "../appinitandstate/AuthState";
import { permissionsToRequest } from "./Permission";
import Auth, { LoginScreen } from "./Auth";
import LocalStoragePersistence from "../localStoragePersistence/common/LocalStoragePersistence";
import { AuthConfig } from "../Config";
import { NavigateFunction } from "react-router-dom";

export default class AuthImpl extends Auth {
  private static readonly defaultThresholdPercent = 0.8;
  private static readonly minThresholdPercent = 0.01;
  private static readonly maxThresholdPercent = 1;
  private accessToken?: string;
  private idToken?: string;
  private expiresAt?: number;
  private refreshTokenAfter?: number;
  private grantedPermissionsString?: string;
  private name?: string; //currently, this is the users email in auth0
  //TODO: Do we always have a subject
  private subject?: string; //something like "auth0|5c...e16e"

  private readonly auth0: WebAuth;
  private readonly refreshThresholdPercent: number;

  constructor(
    refreshThresholdPercent: number = AuthImpl.defaultThresholdPercent,
  ) {
    super();
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.getIdToken = this.getIdToken.bind(this);
    this.renewSession = this.renewSession.bind(this);
    this.getEmail = this.getEmail.bind(this);
    this.getAuth0Subject = this.getAuth0Subject.bind(this);
    this.setSession = this.setSession.bind(this);
    this.getGrantedPermissionsString =
      this.getGrantedPermissionsString.bind(this);
    this.canAttemptSessionRenewal = this.canAttemptSessionRenewal.bind(this);

    this.refreshThresholdPercent = Number.isFinite(refreshThresholdPercent)
      ? Math.min(
          AuthImpl.maxThresholdPercent,
          Math.max(AuthImpl.minThresholdPercent, refreshThresholdPercent),
        )
      : AuthImpl.defaultThresholdPercent;

    this.auth0 = new WebAuth({
      domain: AuthConfig.domain,
      clientID: AuthConfig.clientId,
      redirectUri: AuthConfig.callbackUrl,
      responseType: "id_token token",
      scope:
        "openid profile " + permissionsToRequest.map((p) => p.name).join(" "), //these are the permissions that we ask for. Auth0 decides, which one the user really gets.
      audience: AuthConfig.audience,
    });
  }

  login(_navigate?: NavigateFunction, screen: LoginScreen = "login") {
    this.auth0.authorize(screen === "signup" ? { screen_hint: "signup" } : {});
  }

  handleAuthentication(): Promise<AuthState> {
    return new Promise<AuthState>((resolve) => {
      this.auth0.parseHash((err, authResult) => {
        if (authResult && authResult.accessToken && authResult.idToken) {
          this.setSession(authResult);
          return resolve(AuthState.OkWithOnlineAuthentication);
        } else {
          console.error(err);
          return resolve(AuthState.Failed);
        }
      });
    });
  }

  getAccessToken() {
    return this.accessToken;
  }

  getIdToken() {
    return this.idToken;
  }

  getEmail(): string | undefined {
    return this.name;
  }

  getAuth0Subject(): string {
    if (this.subject === undefined)
      throw new Error("Illegal State: expected not to be undefined.");
    return this.subject;
  }

  private sanitizeExpiresIn(expiresIn: number | undefined): number {
    if (
      expiresIn === undefined ||
      !Number.isFinite(expiresIn) ||
      expiresIn <= 0
    ) {
      console.warn(
        "Auth0 returned an invalid expiresIn value. Token refresh scheduling may fail.",
        {
          expiresIn: expiresIn,
        },
      );
      return 5;
    }
    return expiresIn;
  }

  private setSession(authResult: Auth0DecodedHash) {
    // Set the time that the access token will expire at
    const now = new Date().getTime();
    const expiresInSeconds = this.sanitizeExpiresIn(authResult.expiresIn);
    if (expiresInSeconds < 60) {
      console.warn(
        "Auth0 token lifetime is very short (< 60s). This may cause frequent renewals.",
        {
          expiresIn: expiresInSeconds,
        },
      );
    }
    const expiresAt = expiresInSeconds * 1000 + now;
    const refreshTokenAfter =
      now + (expiresAt - now) * this.refreshThresholdPercent;
    this.accessToken = authResult.accessToken;
    this.idToken = authResult.idToken;
    this.expiresAt = expiresAt;
    this.refreshTokenAfter = refreshTokenAfter;
    this.grantedPermissionsString = authResult.scope;
    LocalStoragePersistence.persist(
      Auth.grantedPermissionsStringLocalStorageKey,
      authResult.scope,
    );
    this.name = authResult.idTokenPayload.name;
    this.subject = authResult.idTokenPayload.sub;
  }

  override canAttemptSessionRenewal(): boolean {
    return LocalStoragePersistence.doesKeyExist(
      Auth.grantedPermissionsStringLocalStorageKey,
    );
  }

  renewSession(resultCallback: (authenticationResult: AuthState) => void) {
    this.auth0.checkSession({}, (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult);
        resultCallback(AuthState.OkWithOnlineAuthentication);
      } else if (err) {
        console.warn("Auth0 checkSession failed:", err);
        // "login_required" or similar means Auth0 explicitly says the user is logged out centrally.
        // Other errors (e.g. timeout) shouldn't blow away the local session immediately.
        if (
          [
            "login_required",
            "consent_required",
            "interaction_required",
          ].includes(err.error)
        ) {
          console.warn(
            `Auth0 session expired: ${err.error} – ${err.error_description}`,
          );
          localStorage.removeItem(
            Auth.grantedPermissionsStringLocalStorageKey.localStorageKey,
          );
          resultCallback(AuthState.SessionExpired);
          return;
        }
        resultCallback(AuthState.Failed);
      } else {
        // Defensive fallback: neither a valid authResult nor an error was provided.
        console.warn("Auth0 checkSession returned no result and no error.");
        resultCallback(AuthState.Failed);
      }
    });
  }

  logout(navigate: NavigateFunction) {
    this.accessToken = undefined;
    this.idToken = undefined;
    this.expiresAt = undefined;
    this.refreshTokenAfter = undefined;
    this.subject = undefined;
    this.name = undefined;
    this.grantedPermissionsString = undefined;
    localStorage.removeItem(
      Auth.grantedPermissionsStringLocalStorageKey.localStorageKey,
    );

    this.auth0.logout({
      returnTo: window.location.origin,
    });

    // navigate to the home route
    navigate("/");
  }

  isAuthenticated(): boolean {
    // Check whether the current time is past the
    // access token's expiry time
    const expiresAt = this.expiresAt;
    return expiresAt !== undefined && new Date().getTime() < expiresAt;
  }

  protected getGrantedPermissionsString(): string | undefined {
    if (this.grantedPermissionsString) {
      return this.grantedPermissionsString;
    } else {
      return LocalStoragePersistence.read<string>(
        Auth.grantedPermissionsStringLocalStorageKey,
      )
        .orTee((e) =>
          console.error(
            "Could not read granted permissions from local storage.",
            e,
          ),
        )
        .unwrapOr(undefined);
    }
  }

  getRefreshTokenAfter(): number | undefined {
    return this.refreshTokenAfter;
  }
}
